import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {DxDataGridComponent} from 'devextreme-angular';
import {custom} from 'devextreme/ui/dialog';
import {Catalog} from '../../../Model/Catalog/Catalog';
import {LoginService} from '../../../Services/login.service';
import {UserManagementService} from '../../../Services/userManagment.service';
import {SystemService} from '../../../Services/system.service';
import {SignalrService} from '../../../Services/signalr.service';
import {CatalogService} from '../../../Services/CatalogManagement/catalog.service';
import {ExportStageService} from '../../../Services/DataProcess/export-stage.service';
import {ImportContainerService} from '../../../Services/DataProcess/import-container.service';

@Component({
    selector: 'app-export-stage',
    templateUrl: './export-stage.component.html',
    styleUrls: ['./export-stage.component.css'],
    standalone: false
})
export class ExportStageComponent implements OnInit {

    @ViewChild(DxDataGridComponent, {static: true})
    dataGrid?: DxDataGridComponent;

    public catalogs: Catalog[] = [];
    public backUpVisible: boolean = false;
    public noDataText: string = '';

    public selectedCatalogId: string = '';
    public selectedCatalogName: string = '';
    public selectedCatalogLogo: string = '';

    private readonly destroyRef = inject(DestroyRef);
    private _loadVersion = 0;
    private logoByCatalogId = new Map<string, string>();

    constructor(
        private readonly router: Router,
        public readonly loginService: LoginService,
        public readonly translate: TranslateService,
        public readonly systemService: SystemService,
        public readonly userManagment: UserManagementService,
        private readonly signalrService: SignalrService,
        public readonly catalogService: CatalogService,
        private readonly exportStageService: ExportStageService,
        private readonly importContainerService: ImportContainerService
    ) {
        this.systemService.currentNavigationTitle = this.translate.instant('Exporte');
        this.noDataText = this.translate.instant('Keine Kataloge in der Exportstage');
        this.duplicateCatalog = this.duplicateCatalog.bind(this);
        this.copyCatalog = this.copyCatalog.bind(this);
        this.createRestorePoint = this.createRestorePoint.bind(this);
        this.removeFromStage = this.removeFromStage.bind(this);
        this.showDeleteDialog = this.showDeleteDialog.bind(this);
        this.refresh = this.refresh.bind(this);
    }

    async ngOnInit(): Promise<void> {
        await this.loadStage();

        this.signalrService.refreshCatalogList$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.loadStage());
    }

    async refresh(): Promise<void> {
        await this.loadStage();
    }

    private async loadStage(): Promise<void> {
        const customerId = this.loginService.currentCustomer.id;
        const version = ++this._loadVersion;

        try {
            const stage = await this.exportStageService.getExportStage(customerId);
            await this.catalogService.setCatalogs(customerId);
            await this.loadCatalogLogos(customerId);
            if (version !== this._loadVersion) return;

            const catalogIds = new Set(stage?.catalogIds ?? []);
            this.catalogs = (this.catalogService.catalogs ?? []).filter(c => catalogIds.has(c.id));
            this.syncSelection();
        } catch {
            if (version === this._loadVersion) {
                this.catalogs = [];
                this.selectedCatalogId = '';
                this.selectedCatalogName = '';
            }
        }
    }

    private async loadCatalogLogos(customerId: string): Promise<void> {
        try {
            const containers = await this.importContainerService.getAll(customerId);
            this.logoByCatalogId.clear();
            for (const container of containers ?? []) {
                if (!container.logoLink) continue;
                for (const catalogId of container.catalogIds ?? []) {
                    this.logoByCatalogId.set(catalogId, container.logoLink);
                }
            }
        } catch {
            this.logoByCatalogId.clear();
        }
    }

    private syncSelection(): void {
        const stillThere = this.catalogs.find(c => c.id === this.selectedCatalogId);
        if (stillThere) {
            this.selectedCatalogName = stillThere.catalogName ?? '';
            this.selectedCatalogLogo = this.logoByCatalogId.get(stillThere.id) ?? '';
            return;
        }

        const first = this.catalogs[0];
        this.selectedCatalogId = first?.id ?? '';
        this.selectedCatalogName = first?.catalogName ?? '';
        this.selectedCatalogLogo = first ? (this.logoByCatalogId.get(first.id) ?? '') : '';
    }

    onFocusedRowChanged(e: any): void {
        const catalog: Catalog = e.row?.data;
        if (!catalog) return;
        this.selectedCatalogId = catalog.id ?? '';
        this.selectedCatalogName = catalog.catalogName ?? '';
        this.selectedCatalogLogo = this.logoByCatalogId.get(catalog.id) ?? '';
    }

    getLanguageFlag(language: string): string {
        if (!language) return '';
        return this.loginService.getLanguageFlag(language.toLowerCase());
    }

    catalogSelected(catalog: Catalog): void {
        this.catalogService.selectedCatalogId = catalog.id;
        this.catalogService.browseRoutePrefix = '';
        this.router.navigate(['/catalog', catalog.customerId, catalog.id, 'info']);
    }

    updateResponsible(data: any): void {
        this.catalogService.setResponsible();
        this.catalogService.catalog = data.data;
        this.catalogService.updateCatalog();
    }

    update(data: any): void {
        this.catalogService.catalog = data.data;
        this.catalogService.updateCatalog();
    }

    duplicateCatalog(e: any): void {
        this.systemService.notifyInfo(this.translate.instant('Der Katalog wird dupliziert'));
        this.catalogService.duplicateCatalog(e.row.data.id);
    }

    copyCatalog(e: any): void {
        this.catalogService.copyCatalogId = e.row.data.id;
        this.catalogService.copyCatalogCustomerId = this.loginService.currentCustomer.id;
        this.systemService.notifyInfo(this.translate.instant('Katalog kopiert. Über "Katalog einfügen" einfügen.'));
    }

    pasteCatalog(): void {
        if (!this.catalogService.copyCatalogId) return;
        this.systemService.notifyInfo(this.translate.instant('Katalog wird eingefügt'));
        this.catalogService.duplicateCatalog(this.catalogService.copyCatalogId, this.catalogService.copyCatalogCustomerId);
    }

    createRestorePoint(e: any): void {
        this.systemService.notifyInfo(this.translate.instant('Ein Wiederherstellungspunkt wird per Job erzeugt'));
        this.catalogService.createRestorePoint(e.row.data.id);
    }

    removeFromStage(e: any): void {
        const catalog: Catalog = e.row.data;
        const confirmDialog = custom({
            title: this.translate.instant('Aus Exportstage entfernen?'),
            messageHtml: `${this.translate.instant('Soll dieser Katalog wirklich aus der Exportstage entfernt werden? Der Katalog selbst bleibt erhalten.')}<br/><br/><b>${catalog.catalogName}</b>`,
            buttons: [
                {text: this.translate.instant('Ja'), onClick: () => true},
                {text: this.translate.instant('Nein'), onClick: () => false}
            ]
        });

        confirmDialog.show().then(async (confirmed: boolean) => {
            if (!confirmed) return;
            const customerId = this.loginService.currentCustomer.id;
            try {
                await this.exportStageService.removeCatalogFromStage(catalog.id, customerId);
                this.systemService.notifyInfo(this.translate.instant('Katalog aus der Exportstage entfernt.'));
                await this.loadStage();
            } catch {
                this.systemService.notifyInfo(this.translate.instant('Fehler beim Entfernen aus der Exportstage.'));
            }
        });
    }

    showDeleteDialog(e: any): void {
        const catalog: Catalog = e.row.data;
        const confirmDialog = custom({
            title: this.translate.instant('Katalog löschen'),
            messageHtml: `${this.translate.instant('Wollen Sie den Katalog wirklich löschen?')}<br/><br/><b>${catalog.catalogName}</b>`,
            buttons: [
                {text: this.translate.instant('Ja'), onClick: () => true},
                {text: this.translate.instant('Nein'), onClick: () => false}
            ]
        });

        confirmDialog.show().then(async (confirmed: boolean) => {
            if (!confirmed) return;
            const customerId = this.loginService.currentCustomer.id;
            try {
                this.catalogService.remCatalog(catalog.id);
                await this.exportStageService.removeCatalogFromStage(catalog.id, customerId);
                this.systemService.notifyInfo(this.translate.instant('Katalog wird gelöscht'));
                await this.loadStage();
            } catch {
                this.systemService.notifyInfo(this.translate.instant('Fehler beim Löschen des Katalogs'));
            }
        });
    }

    canView(name: string): boolean {
        return this.userManagment.canView(name);
    }

    canEdit(name: string): boolean {
        return this.userManagment.canEdit(name);
    }

    mimedate(): string {
        return '?' + new Date().toDateString();
    }

    showBackUp(): void {
        this.backUpVisible = true;
    }
}
